/**
 * C14 · company data & account view-model — pure display/shaping helpers kept
 * out of the .vue so node:test can cover the logic directly (the companies repo
 * has no SFC test runner). Covers: seeding the editable profile form from the
 * GET /companies/<own> entity + shaping the PATCH body, the HR-users status → tag
 * map, avatar initials/rotation + email-domain derivation for the front-only
 * users list, and the add-user + change-password validators (§2 rules). Plain
 * CommonJS.
 */
var USER_STATUS_TAG = {
  active: 'ok',
  suspended: 'review',
};

var AVATAR_ROTATION = ['', 'a2', 'a3'];

var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
var PASSWORD_MIN = 8;
// The server's ceiling (ChangePasswordDto / CompanyHrDto @MaxLength(128)). The
// form had no maximum, so a longer passphrase produced an unexplained 400.
var PASSWORD_MAX = 128;

/** user status -> .tag modifier class (active = ok/green, suspended = review/amber). */
function userStatusTag(status) {
  return USER_STATUS_TAG[status] || 'muted';
}

/** avatar accent class by row index, cycling ''/a2/a3 like the mockup. */
function avatarClass(index) {
  var i = Number(index) || 0;
  return AVATAR_ROTATION[i % AVATAR_ROTATION.length];
}

/** 2-letter avatar initials — first two characters of the first word (like the mockup). */
function initials(name) {
  var s = String(name == null ? '' : name).trim();
  if (!s) return '؟';
  return s.split(/\s+/)[0].slice(0, 2);
}

/** domain part of an email, or a fallback when absent/malformed. */
function emailDomain(email, fallback) {
  var fb = fallback || 'company.example';
  var raw = String(email == null ? '' : email);
  var at = raw.indexOf('@');
  if (at === -1) return fb;
  var domain = raw.slice(at + 1).trim();
  return domain || fb;
}

// H2 (HR issues 2026-10-16): seedUsers() and nextUserId() are GONE.
//
// seedUsers() invented three people — «سارة مصطفى», «محمد جمال», «هالة رمزي» —
// with addresses built from the company's own email domain, and the screen
// displayed them as if they were the company's real HR logins. nextUserId()
// appended more of the same to local state. The panel reads
// GET /companies/:id/hr-users now, so there is nothing to fake.

/** seed the editable profile form from the GET /companies/<own> entity. */
function buildProfileForm(company) {
  var c = company || {};
  return {
    name: c.name == null ? '' : c.name,
    commercialReg: c.commercialReg == null ? '' : c.commercialReg,
    taxId: c.taxId == null ? '' : c.taxId,
    contactPhone: c.contactPhone == null ? '' : c.contactPhone,
    contactEmail: c.contactEmail == null ? '' : c.contactEmail,
  };
}

/** shape the PATCH /companies/:id body: name (required) + only the populated
 *  optionals. Empty optionals are DROPPED, not sent as '' — the backend
 *  UpdateCompanyDto marks contactEmail @IsEmail, so a '' would 400. */
/**
 * Form -> PATCH /companies/:id body.
 *
 * H3: an emptied optional field is sent as `null`, not dropped. PATCH reads an
 * omitted key as "leave this alone", so skipping empties meant a wrong tax id,
 * commercial registration, phone or contact email could be typed in but never
 * REMOVED — clearing the box and saving silently kept the old value.
 * UpdateCompanyDto marks all four @IsOptional(), which in class-validator skips
 * the remaining validators for null, and the service passes the value straight
 * to Prisma — so null is exactly how you clear one (an e2e test pins it).
 *
 * `name` is required and never nulled. Derived and identity fields
 * (employeesCount, id, insurerId, …) are never sent: the count is computed on
 * read, and the rest are not editable.
 */
function buildProfilePayload(form) {
  var f = form || {};
  var payload = { name: String(f.name == null ? '' : f.name).trim() };
  var optional = ['commercialReg', 'taxId', 'contactPhone', 'contactEmail'];
  for (var i = 0; i < optional.length; i++) {
    var k = optional[i];
    var v = String(f[k] == null ? '' : f[k]).trim();
    // emails are stored lowercase elsewhere (the API lowercases HR logins), so
    // normalise here too rather than keeping two conventions in one table
    if (k === 'contactEmail') v = v.toLowerCase();
    payload[k] = v === '' ? null : v;
  }
  return payload;
}

/** validate the profile form: name required, email shape when present. */
function validateProfile(form) {
  var f = form || {};
  var errors = {};
  if (!String(f.name == null ? '' : f.name).trim()) errors.name = 'required';
  var email = String(f.contactEmail == null ? '' : f.contactEmail).trim();
  if (email && !EMAIL_RE.test(email)) errors.contactEmail = 'email';
  return { valid: Object.keys(errors).length === 0, errors: errors };
}

/** validate the add-HR-user form: name required, email required + shape. */
/**
 * Validate a NEW HR login against CompanyHrDto, field for field:
 *   fullName  2..120
 *   email     IsEmail, <= 191
 *   password  8..128, at least one letter AND one digit
 *
 * The old validateUser() checked only "name non-empty" and a loose email — and
 * the form had no password field at all, so even an enabled button could never
 * have created a login. Mirroring the server rule exactly is the point: a rule
 * that is stricter rejects passwords the server would accept, and a rule that is
 * looser produces a 400 the form cannot explain.
 */
function validateHrUser(form) {
  var f = form || {};
  var errors = {};

  var name = String(f.fullName == null ? '' : f.fullName).trim();
  if (!name) errors.fullName = 'required';
  else if (name.length < 2) errors.fullName = 'nameTooShort';
  else if (name.length > 120) errors.fullName = 'nameTooLong';

  var email = String(f.email == null ? '' : f.email).trim();
  if (!email) errors.email = 'required';
  else if (!EMAIL_RE.test(email)) errors.email = 'email';
  else if (email.length > 191) errors.email = 'emailTooLong';

  var pw = String(f.password == null ? '' : f.password);
  if (!pw) errors.password = 'required';
  else if (pw.length < PASSWORD_MIN) errors.password = 'tooShort';
  else if (pw.length > PASSWORD_MAX) errors.password = 'tooLong';
  else if (!/[A-Za-z]/.test(pw) || !/\d/.test(pw)) errors.password = 'weak';

  return { valid: Object.keys(errors).length === 0, errors: errors };
}

var HR_USER_API_ERRORS = {
  EMAIL_TAKEN: 'emailTaken',
  ROLE_MISSING: 'roleMissing',
  COMPANY_CONTEXT_MISSING: 'forbidden',
};

/** Server error -> i18n key, so the panel never shows a raw backend string. */
function hrUserErrorKey(err) {
  var e = err || {};
  var res = e.response;
  if (!res) return 'network';
  var code = res.data && res.data.code;
  if (code && HR_USER_API_ERRORS[code]) return HR_USER_API_ERRORS[code];
  if (res.status === 409) return 'emailTaken';
  if (res.status === 403) return 'forbidden';
  // own-company scoping answers 404 (not 403) for another company's logins
  if (res.status === 404) return 'forbidden';
  if (res.status === 400 || res.status === 422) return 'validation';
  return 'generic';
}

/**
 * validate the change-password form: all required, new >= 8 chars with a digit
 * and an uppercase letter, confirm must match the new one.
 */
function validatePassword(form) {
  var f = form || {};
  var errors = {};
  var current = String(f.current == null ? '' : f.current);
  var next = String(f.next == null ? '' : f.next);
  var confirm = String(f.confirm == null ? '' : f.confirm);
  if (!current) errors.current = 'required';
  if (!next) errors.next = 'required';
  // H3: this required an UPPERCASE letter, which the server does not — it asks
  // for a letter and a digit (ChangePasswordDto / CompanyHrDto). The form was
  // rejecting passwords the backend would have accepted. It also had no maximum,
  // so a long passphrase produced an unexplained 400 at 129 characters.
  else if (next.length < PASSWORD_MIN) errors.next = 'tooShort';
  else if (next.length > PASSWORD_MAX) errors.next = 'tooLong';
  else if (!/[A-Za-z]/.test(next) || !/\d/.test(next)) errors.next = 'weak';
  if (!confirm) errors.confirm = 'required';
  else if (next && confirm !== next) errors.confirm = 'mismatch';
  return { valid: Object.keys(errors).length === 0, errors: errors };
}

module.exports = {
  USER_STATUS_TAG: USER_STATUS_TAG,
  PASSWORD_MIN: PASSWORD_MIN,
  userStatusTag: userStatusTag,
  avatarClass: avatarClass,
  initials: initials,
  emailDomain: emailDomain,
  validateHrUser: validateHrUser,
  hrUserErrorKey: hrUserErrorKey,
  HR_USER_API_ERRORS: HR_USER_API_ERRORS,
  buildProfileForm: buildProfileForm,
  buildProfilePayload: buildProfilePayload,
  validateProfile: validateProfile,
  validatePassword: validatePassword,
};
